import { generateId, generateString } from "../../utils/zone.utils";
import { useSubscribeDOMDestroyed, useSubscribeDOMRendered } from "../observer/useDomObserver";
import useMap from "../utils/useMap";
import useCustomComponent from "./useCustomComponent";
import useSandbox from "./useSandBox";

/**
 * Description placeholder
 *
 * @param {string} htmlString
 * @returns {Document}
 */
const parseHTML = (htmlString) => {
    const parser = new DOMParser();
    return parser.parseFromString(htmlString, 'text/html');
}


/**
 * Description placeholder
 *
 * @param {string} htmlContent
 * @param {HTMLElement} root
 * @returns {Promise}
 */
const injectScript = (() => {

    const scriptPool = useMap();

    const elmScript = document.createElement("div");
    document.body.appendChild(elmScript);

    const injectedTags = [];

    const revokeScript = (src) => {
        if (scriptPool.has(src)) {
            const item = scriptPool.get(src);
            item.count--;
            if (item.count == 0) {
                elmScript.remove(item.tag);
            } else {
                scriptPool.set(src, item);
            }
        }
    }

    /**
    *
    * @param {HTMLScriptElement} scriptTag
    * @returns {Promise}
    */
    const loadScript = ({ src } = scriptTag, root) => {
        return new Promise(next => {
            useSubscribeDOMDestroyed(root, () => {
                revokeScript(src);
            })
            if (scriptPool.has(src)) {
                scriptPool.get(src).count++;
                next();
                return;
            }
            const tag = document.createElement('script');
            tag.src = src;

            tag.addEventListener('load', next)
            elmScript.appendChild(tag);
            scriptPool.set(src, { count: 1, tag });
        })
    }

    const isScriptZone = (tag) => {
        const attr = tag.getAttribute('rczscript');
        return typeof attr === 'string';
    }

    return (htmlContent, root) => {
        const doc = parseHTML(htmlContent);
        const scriptTags = doc.querySelectorAll("script");

        const tasks = Array.from(scriptTags).reduce((output, s) => {
            if (!isScriptZone(s)) {
                output.push(loadScript(s, root));

            };
            return output;
        }, [])

        const rs = {
            revokeScripts: () => {
                while (injectedTags.length > 0) {
                    const src = injectedTags.shift();
                    // revokeScript(src)
                }
            }
        }

        return tasks.length == 0 ? Promise.resolve(rs) : Promise.all(tasks).then(() => Promise.resolve(rs));
    }
})()


/**
 * Description placeholder
 *
 * @param {*} htmlContent
 * @param {*} root
 * @returns {Promise<{rootId: string}>}
 */
const injectTemplate = (htmlContent, root) => {
    const doc = parseHTML(htmlContent);
    const template = doc.querySelector("template");

    if (!template) {
        throw "Missing <template> tag";
    }

    const subRoot = document.createElement('div');
    const rootId = generateString(6, 'rczRoot');
    subRoot.setAttribute('rczRoot', rootId);
    subRoot.innerHTML = template.innerHTML;
    const fragment = document.createDocumentFragment();

    const children = Array.from(subRoot.children);
    for (let i in children) {
        fragment.appendChild(children[i]);
    }

    root.appendChild(fragment);
    return Promise.resolve({ rootId, subRoot: root });
}


/**
 *
 * @param {string} body
 * @param {HTMLElement} root
 * @param {string} rootId
 * @param {object} props
 * @returns {{ url: any; revokeCode: () => void; }}
 */
const injectCode = (() => {
    const codePool = document.createElement("div");
    document.body.appendChild(codePool);

    return (body, root, rootId, props) => {
        const doc = parseHTML(body);
        if (!doc.querySelector('script[rczscript]')) { return Promise.resolve({ revokeCode: () => { } }) };

        const jsSource = doc.querySelector('script[rczscript]').innerHTML;
        const { newBox } = useSandbox();
        const { code } = newBox(jsSource, { root });
        const blob = new Blob([code], { type: "text/javascript" });

        const url = window.URL.createObjectURL(blob);

        return new Promise(next => {
            const scriptTag = document.createElement('script');
            scriptTag.src = url;
            scriptTag.setAttribute("rootId", rootId)
            scriptTag.addEventListener('load', () => {
                const rs = {
                    revokeCode: () => {
                        window.URL.revokeObjectURL(url)

                        if (codePool.contains(scriptTag)) {
                            codePool.removeChild(scriptTag)
                        }
                    }
                };
                if (!document.querySelector(`[rczroot='${rootId}']`)) {
                    rs.revokeCode();
                }

                next(rs)
            });
            codePool.appendChild(scriptTag);
        })
    }

})()


/**
 * Description placeholder
 *
 * @type {(src: any) => Promise<string>}
 */
const fetchSource = (() => {
    const caching = useMap();

    return async (src) => {
        if (caching.has(src)) {
            return caching.get(src);
        }
        const response = await fetch(src);
        const body = await response.text();
        caching.set(src, body);
        return body;
    }
})();

/**
 * Description placeholder
 *
 * @param {string} src
 * @param {() =>void} onReady
 */
const useFetchZone = (() => {

    const queue = [];
    let isWorking = false;

    const runJob = async ({ src = '', root = document.body, onReady, props, abortController = {} }) => {

        const d = document.createElement('div');
        d.innerHTML = 'Loading...';
        root.appendChild(d);

        try {
            isWorking = true;

            const templateContent = await fetchSource(src);
            const { rootId, subRoot } = await injectTemplate(templateContent, root);
            subRoot.style.display = 'none';
            const { revokeScripts } = await injectScript(templateContent, subRoot);
            const { revokeCode } = await injectCode(templateContent, subRoot, rootId, {});
            subRoot.style.display = '';

            root.removeChild(d);

            useSubscribeDOMRendered(root, () => {
                onReady();
            });

            useSubscribeDOMDestroyed(root, () => {
                revokeCode();
                revokeScripts();
            })

            useSubscribeDOMDestroyed(subRoot, () => {
                revokeCode();
                revokeScripts();
            }, null, props, root)

            abortController.abort = () => {
                console.log("Abort");
                revokeCode();
                revokeScripts();
            }

            if (queue.length > 0) {
                runJob(queue.shift());
            } else {
                isWorking = false;
            }

        } catch (ex) {
            d.innerHTML = `Has Error! '${src}'`
            throw new Error(`Define Component: Path[${src}] - ${ex}`);
        }
    }

    return ({ src = '', root = document.body, onReady, props, abortController }) => {
        queue.push({ src, root, onReady, props, abortController });
        if (!isWorking) {
            runJob(queue.shift());
        }
    }
})();

export default useFetchZone;